import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
const STORAGE_KEY = 'opus-theme';
const ThemeContext = createContext(null);
function systemPrefers() {
    return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
function resolve(choice) {
    return choice === 'system' ? systemPrefers() : choice;
}
function applyToDocument(resolved) {
    const root = document.documentElement;
    root.classList.toggle('dark', resolved === 'dark');
    root.style.colorScheme = resolved;
}
/** Class-strategy theme provider — same behavior the old next-themes setup had. */
export function ThemeProvider({ children, defaultTheme = 'system' }) {
    const [theme, setThemeState] = useState(() => {
        if (typeof window === 'undefined')
            return defaultTheme;
        const stored = window.localStorage.getItem(STORAGE_KEY);
        return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : defaultTheme;
    });
    const [resolved, setResolved] = useState(() => resolve(theme));
    useEffect(() => {
        const r = resolve(theme);
        setResolved(r);
        applyToDocument(r);
        if (theme === 'system') {
            const mq = window.matchMedia('(prefers-color-scheme: dark)');
            const onChange = () => {
                const nr = systemPrefers();
                setResolved(nr);
                applyToDocument(nr);
            };
            mq.addEventListener('change', onChange);
            return () => mq.removeEventListener('change', onChange);
        }
    }, [theme]);
    const setTheme = useCallback((t) => {
        setThemeState(t);
        try {
            window.localStorage.setItem(STORAGE_KEY, t);
        }
        catch {
            /* storage unavailable — theme stays for the session */
        }
    }, []);
    const toggle = useCallback(() => {
        setTheme(resolve(theme) === 'dark' ? 'light' : 'dark');
    }, [theme, setTheme]);
    const value = useMemo(() => ({ theme, resolved, setTheme, toggle }), [theme, resolved, setTheme, toggle]);
    return _jsx(ThemeContext.Provider, { value: value, children: children });
}
export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx)
        throw new Error('useTheme must be used inside <ThemeProvider>');
    return ctx;
}
/** Sun/moon toggle with the mounted guard + aria-label (audit a11y note). */
export function ThemeToggle({ className }) {
    const { resolved, toggle } = useTheme();
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);
    if (!mounted)
        return _jsx("span", { className: className, "aria-hidden": true });
    return (_jsx("button", { type: "button", className: className, onClick: toggle, "aria-label": resolved === 'dark' ? 'switch to light theme' : 'switch to dark theme', children: resolved === 'dark' ? '☀' : '☾' }));
}
//# sourceMappingURL=theme.js.map